/**
 * Creator v2 — state and navigation, as pure functions.
 *
 * One screen asks one thing (decision D1), so "where am I and where do I go next"
 * is most of the logic. It lives here, away from the components, so every path
 * — including the clue-count limits — is unit-tested without rendering anything.
 */
import { MIN_CLUES, MAX_CLUES, LIMITS, MIN_WORD_LENGTH, STEPS } from "./constants";

export const emptyClue = () => ({
  question: "",
  answer: "",
  hint: "",
  title: "",
  celebrationType: "default",
  celebrationContent: "",
});

/**
 * @param {{ puzzleId: string, recipientName?: string, theme?: string|null }} seed
 */
export function initialState({ puzzleId, recipientName = "", theme = null }) {
  return {
    version: 2,
    puzzleId,
    step: "name",
    recipientName,
    theme,
    clues: [],
    clueIndex: 0,
    // Where "back" from the question screen leads (vibe, the previous clue's
    // reward, or the list) — set whenever a clue is opened.
    returnTo: "vibe",
    finalMessage: "",
    finale: null,
    // Sealed by default: it is how every puzzle behaves today, and "No" is one tap.
    seal: { enabled: true, word: "", hint: "" },
    title: "",
  };
}

const clip = (value, max) => String(value ?? "").slice(0, max);
const isBlank = (s) => String(s ?? "").trim() === "";

export const isClueComplete = (c) => !!c && !isBlank(c.question) && !isBlank(c.answer);
export const completeClues = (state) => state.clues.filter(isClueComplete);
export const currentClue = (state) => state.clues[state.clueIndex] || null;
export const segmentOf = (step) => STEPS[step] ?? 0;
export const canAddClue = (state) => completeClues(state).length < MAX_CLUES;
export const hasEnoughClues = (state) => completeClues(state).length >= MIN_CLUES;

const needsDetail = (type) => type === "message" || type === "photo" || type === "video" || type === "song";

/** Can the user leave this screen with its primary action? */
export function canContinue(state) {
  const clue = currentClue(state);
  switch (state.step) {
    case "name":
      return !isBlank(state.recipientName);
    case "vibe":
      return !!state.theme;
    case "question":
      return !!clue && !isBlank(clue.question);
    case "answer":
      return !!clue && !isBlank(clue.answer);
    case "celebrationDetail":
      return !!clue && !isBlank(clue.celebrationContent);
    case "list":
      return hasEnoughClues(state);
    case "reveal":
      return !isBlank(state.finalMessage);
    case "word":
      return state.seal.word.trim().length >= MIN_WORD_LENGTH;
    default:
      return true;
  }
}

/** Drop a clue the user opened and left untouched (e.g. backed out of "add"). */
// Drops the clue being edited when nothing was written in it. The last clue
// is kept unless `allowNone`: going back to the vibe screen from an untouched
// first clue has to leave no clue, or continuing would open an empty list.
function withoutEmptyCurrent(state, allowNone = false) {
  const clue = currentClue(state);
  if (!clue || !isBlank(clue.question) || !isBlank(clue.answer)) return state;
  if (!allowNone && state.clues.length <= 1) return state;
  const clues = state.clues.filter((_, i) => i !== state.clueIndex);
  return { ...state, clues, clueIndex: Math.max(0, Math.min(state.clueIndex, clues.length - 1)) };
}

function openNewClue(state, returnTo) {
  if (!canAddClue(state)) return state;
  const clues = [...state.clues, emptyClue()];
  return { ...state, clues, clueIndex: clues.length - 1, returnTo, step: "question" };
}

/**
 * The primary action of the current screen.
 * `intent` distinguishes the screens that offer more than one way forward:
 *   reward → "add" | "done" | "hint" | "name"      list → "add" | "reveal"
 */
export function next(state, intent) {
  if (!canContinue(state) && intent !== "add" && intent !== "hint" && intent !== "name") return state;
  const clue = currentClue(state);
  switch (state.step) {
    case "name":
      // A vibe already chosen (picked on Home, or kept from a draft) skips the
      // vibe screen; back from the first clue still leads to it.
      return state.theme ? next({ ...state, step: "vibe" }) : { ...state, step: "vibe" };
    case "vibe":
      return state.clues.length ? { ...state, step: "list" } : openNewClue(state, "vibe");
    case "question":
      return { ...state, step: "answer" };
    case "answer":
      return { ...state, step: "celebration" };
    case "celebration":
      return { ...state, step: needsDetail(clue && clue.celebrationType) ? "celebrationDetail" : "reward" };
    case "celebrationDetail":
      return { ...state, step: "reward" };
    case "reward":
      if (intent === "add") return openNewClue(state, "reward");
      if (intent === "hint") return { ...state, step: "hint" };
      if (intent === "name") return { ...state, step: "clueName" };
      // Below the minimum the only way on is another clue (decision D2) — the UI
      // doesn't render "That's all my clues" there, and this refuses it anyway.
      if (!hasEnoughClues(state)) return state;
      return { ...state, step: "list" };
    case "hint":
    case "clueName":
      return { ...state, step: "reward" };
    case "list":
      if (intent === "add") return openNewClue(state, "list");
      return { ...state, step: "reveal" };
    case "reveal":
      return { ...state, step: "seal" };
    case "seal":
      return { ...state, step: state.seal.enabled ? "word" : "ready" };
    case "word":
      return { ...state, step: "ready" };
    case "ready":
      return { ...state, step: "share" };
    default:
      return state;
  }
}

/** The back button. Always lands on a screen that makes sense for what's filled in. */
export function back(state) {
  const clue = currentClue(state);
  switch (state.step) {
    case "vibe":
      return { ...state, step: "name" };
    case "question": {
      const dest = state.returnTo || "vibe";
      const trimmed = withoutEmptyCurrent(state, dest === "vibe");
      if (dest === "reward") {
        // Came from the previous clue's reward screen.
        return { ...trimmed, step: "reward", clueIndex: Math.max(0, trimmed.clues.length - 1) };
      }
      return { ...trimmed, step: dest };
    }
    case "answer":
      return { ...state, step: "question" };
    case "celebration":
      return { ...state, step: "answer" };
    case "celebrationDetail":
      return { ...state, step: "celebration" };
    case "reward":
      return { ...state, step: needsDetail(clue && clue.celebrationType) ? "celebrationDetail" : "celebration" };
    case "hint":
    case "clueName":
      return { ...state, step: "reward" };
    case "list":
      return { ...state, step: "reward", clueIndex: Math.max(0, state.clues.length - 1) };
    case "reveal":
      return { ...state, step: "list" };
    case "seal":
      return { ...state, step: "reveal" };
    case "word":
      return { ...state, step: "seal" };
    case "ready":
      return { ...state, step: state.seal.enabled ? "word" : "seal" };
    default:
      return state;
  }
}

function updateClue(state, patch) {
  const clues = state.clues.map((c, i) => (i === state.clueIndex ? { ...c, ...patch } : c));
  return { ...state, clues };
}

export function reducer(state, action) {
  switch (action.type) {
    case "hydrate":
      return action.state;
    case "next":
      return next(state, action.intent);
    case "back":
      return back(state);
    case "setName":
      return { ...state, recipientName: clip(action.value, LIMITS.name) };
    case "setTheme":
      return { ...state, theme: action.value };
    case "setQuestion":
      return updateClue(state, { question: clip(action.value, LIMITS.question) });
    case "setAnswer":
      return updateClue(state, { answer: clip(action.value, LIMITS.answer) });
    case "setHint":
      return updateClue(state, { hint: clip(action.value, LIMITS.hint) });
    case "setClueTitle":
      return updateClue(state, { title: clip(action.value, LIMITS.clueTitle) });
    case "setCelebration": {
      const clue = currentClue(state);
      if (!clue || clue.celebrationType === action.value) return state;
      // Switching type discards the other type's content (a photo URL is not a message).
      return updateClue(state, { celebrationType: action.value, celebrationContent: "" });
    }
    case "setCelebrationContent":
      return updateClue(state, {
        celebrationContent: limitCelebration(currentClue(state), action.value),
      });
    case "editClue":
      if (action.index < 0 || action.index >= state.clues.length) return state;
      return { ...state, clueIndex: action.index, returnTo: "list", step: "question" };
    case "deleteClue": {
      if (action.index < 0 || action.index >= state.clues.length) return state;
      const clues = state.clues.filter((_, i) => i !== action.index);
      return { ...state, clues, clueIndex: Math.max(0, Math.min(state.clueIndex, clues.length - 1)) };
    }
    case "moveClue": {
      const { from, to } = action;
      if (from === to || from < 0 || to < 0 || from >= state.clues.length || to >= state.clues.length) return state;
      const clues = [...state.clues];
      const [moved] = clues.splice(from, 1);
      clues.splice(to, 0, moved);
      return { ...state, clues };
    }
    case "setFinalMessage":
      return { ...state, finalMessage: clip(action.value, LIMITS.finalMessage) };
    case "setFinale":
      return { ...state, finale: action.value || null };
    case "setSealEnabled":
      return { ...state, seal: { ...state.seal, enabled: !!action.value } };
    case "setWord":
      return { ...state, seal: { ...state.seal, word: clip(action.value, LIMITS.word) } };
    case "setWordHint":
      return { ...state, seal: { ...state.seal, hint: clip(action.value, LIMITS.wordHint) } };
    case "setTitle":
      return { ...state, title: clip(action.value, LIMITS.title) };
    case "go":
      return STEPS[action.step] === undefined ? state : { ...state, step: action.step };
    default:
      return state;
  }
}

// A typed message is capped; media URLs and video links pass through untouched.
function limitCelebration(clue, value) {
  return clue && clue.celebrationType === "message" ? clip(value, LIMITS.message) : String(value ?? "");
}
